import type {
  ScoutPrivateDocumentExtractionStatus,
  ScoutPrivateDocumentIngestionMode,
} from "generated-graphql/graphql";

import pluralize from "pluralize";

/** Inline multimodal path — must match encamp-scout-connection SCOUT_NATIVE_MAX_PDF_BYTES */
export const SCOUT_NATIVE_MAX_PDF_BYTES = 30 * 1024 * 1024;

/** Large-PDF extraction path — must match encamp-scout-connection SCOUT_LARGE_PDF_MAX_BYTES */
export const SCOUT_LARGE_PDF_MAX_BYTES = 200 * 1024 * 1024;

export const SCOUT_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const SCOUT_MAX_TEXT_BYTES = 200 * 1024;
export const SCOUT_MAX_ATTACHMENTS = 5;

const toMiB = (bytes: number): string => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

export function validateScoutAttachmentFile(file: File): string | undefined {
  const mimeType = file.type.toLowerCase();

  if (mimeType === "application/pdf") {
    if (file.size > SCOUT_LARGE_PDF_MAX_BYTES) {
      return `File is larger than 200 MB (${toMiB(file.size)}) and is not supported.`;
    }
    return undefined;
  }

  if (
    mimeType === "image/png" ||
    mimeType === "image/jpeg" ||
    mimeType === "image/gif" ||
    mimeType === "image/webp"
  ) {
    if (file.size > SCOUT_MAX_IMAGE_BYTES) {
      return `File is larger than 5 MB (${toMiB(file.size)}).`;
    }
    return undefined;
  }

  if (mimeType === "text/plain") {
    if (file.size > SCOUT_MAX_TEXT_BYTES) {
      return "File is larger than 200 KB.";
    }
    return undefined;
  }

  return "File type is unsupported. Scout currently supports PDFs, images, and plain text.";
}

export function formatScoutDocumentSizeBytes(sizeBytes: number | undefined): string | undefined {
  if (sizeBytes === undefined || sizeBytes <= 0) {
    return undefined;
  }

  if (sizeBytes >= 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (sizeBytes >= 1024) {
    return `${(sizeBytes / 1024).toFixed(0)} KB`;
  }

  return `${sizeBytes} B`;
}

export function userFriendlyScoutMediaType(mediaType: string): string {
  switch (mediaType) {
    case "application/pdf":
      return "PDF";
    case "image/png":
      return "PNG";
    case "image/jpeg":
      return "JPEG";
    default:
      return mediaType;
  }
}

export function formatScoutFileHoverMetadata(input: {
  mediaType?: string;
  sizeBytes?: number;
}): string | undefined {
  const typeLabel =
    input.mediaType !== undefined ? formatScoutFileHoverTypeLabel(input.mediaType) : undefined;
  const sizeLabel = formatScoutDocumentSizeBytes(input.sizeBytes);

  if (typeLabel && sizeLabel) {
    return `${typeLabel} · ${sizeLabel}`;
  }

  return typeLabel ?? sizeLabel;
}

function formatScoutFileHoverTypeLabel(mediaType: string): string | undefined {
  const normalizedMediaType = mediaType.toLowerCase();
  if (!normalizedMediaType || normalizedMediaType === "application/octet-stream") {
    return undefined;
  }

  const friendlyLabel = userFriendlyScoutMediaType(normalizedMediaType);
  if (friendlyLabel.includes("/")) {
    return undefined;
  }

  return friendlyLabel;
}

export function formatScoutFileMessageSize(sizeBytes: number | undefined): string | undefined {
  if (sizeBytes === undefined || sizeBytes <= 0) {
    return undefined;
  }

  if (sizeBytes >= 1024 * 1024) {
    return `${Math.round(sizeBytes / (1024 * 1024))} mb`;
  }

  if (sizeBytes >= 1024) {
    return `${Math.round(sizeBytes / 1024)} kb`;
  }

  return `${sizeBytes} b`;
}

export function formatScoutFileMessageMetadata(input: {
  sizeBytes?: number;
  pageCount?: number | null;
}): string | undefined {
  const sizeLabel = formatScoutFileMessageSize(input.sizeBytes);
  const pageCount = input.pageCount;
  const pageLabel =
    pageCount !== undefined && pageCount !== null && pageCount > 0
      ? `${pageCount} pages`
      : undefined;

  if (sizeLabel && pageLabel) {
    return `${sizeLabel} • ${pageLabel}`;
  }

  return sizeLabel ?? pageLabel;
}

export function inferScoutFileDetectedLabel(filename: string | undefined): string | undefined {
  if (!filename) {
    return undefined;
  }

  const text = filename.toLowerCase();

  if (
    text.includes("air permit") ||
    text.includes("title v") ||
    text.includes("titlev") ||
    text.includes("operating permit")
  ) {
    return "Air Permit";
  }

  if (text.includes("sds") || text.includes("safety data sheet") || text.includes("msds")) {
    return "Safety Data Sheet";
  }

  if (text.includes("tier ii") || text.includes("tier 2") || text.includes("epcra")) {
    return "Tier II Report";
  }

  if (text.includes("npdes") || text.includes("stormwater") || text.includes("swppp")) {
    return "Water Permit";
  }

  if (text.includes("rcra") || text.includes("hazardous waste") || text.includes("manifest")) {
    return "Hazardous Waste Document";
  }

  if (text.includes("inspection") || text.includes("notice of violation")) {
    return "Inspection Document";
  }

  if (text.includes("permit")) {
    return "Environmental Permit";
  }

  return undefined;
}

export function formatScoutFileSecondaryText(input: {
  mediaType: string;
  sizeBytes?: number;
  pageCount?: number | null;
  ingestionMode?: ScoutPrivateDocumentIngestionMode | string;
  extractionStatus?: ScoutPrivateDocumentExtractionStatus | string;
  extractionProgress?: {
    pagesCompleted: number;
    pagesTotal?: number | null;
  } | null;
}): { documentInfo: string; extractionInfo?: string } {
  const typeLabel = userFriendlyScoutMediaType(input.mediaType);
  const sizeLabel = formatScoutDocumentSizeBytes(input.sizeBytes);
  const baseLabel = sizeLabel
    ? `${typeLabel} · ${sizeLabel} ${input.pageCount ? `· ${input.pageCount} ${pluralize("page", input.pageCount)}` : ""}`
    : typeLabel;

  if (input.ingestionMode !== "LARGE_PDF_EXTRACTION") {
    return { documentInfo: baseLabel };
  }

  if (input.extractionStatus === "PREVIEW_RUNNING") {
    return { documentInfo: baseLabel, extractionInfo: "Scanning first pages…" };
  }

  if (input.extractionStatus === "AWAITING_CONFIRMATION") {
    return { documentInfo: baseLabel, extractionInfo: "Confirm to start full analysis" };
  }

  if (input.extractionStatus === "FULL_ANALYSIS_DECLINED") {
    return { documentInfo: baseLabel, extractionInfo: "Using preview only" };
  }

  if (input.extractionStatus === "PENDING" || input.extractionStatus === "RUNNING") {
    const progress = input.extractionProgress;
    if (progress?.pagesTotal && progress.pagesTotal > 0) {
      return {
        documentInfo: baseLabel,
        extractionInfo: `Analyzing page ${progress.pagesCompleted} of ${progress.pagesTotal}`,
      };
    }
    return { documentInfo: baseLabel, extractionInfo: "Full analysis in progress…" };
  }

  if (input.extractionStatus === "FAILED") {
    return { documentInfo: baseLabel, extractionInfo: "Analysis failed" };
  }

  if (input.extractionStatus === "NOT_ATTEMPTED") {
    return { documentInfo: baseLabel, extractionInfo: "Large file" };
  }

  return { documentInfo: baseLabel };
}
